// js/lib/theme.js
// App-level theme management.
//
// Themes are applied by setting data-theme on <html>.
// Every component always uses the same --color-* semantic tokens;
// the theme block in tokens.css remaps those tokens to the chosen palette.
//
// Available themes:
//   "gold-dark"  — Dark gold (editorial dark)
//   "gold-light" — Light gold (default for all surfaces)
//
// Storage keys:
//   rewardlix_ownerTheme   — owner's personal app theme (localStorage)
//   rewardlix_themePref    — customer's personal app theme (localStorage)

const DEFAULT_THEME = "gold-light";
const VALID_THEMES = ["gold-dark", "gold-light"];
const CUSTOMER_PREF_KEY = "rewardlix_themePref";
const OWNER_PREF_KEY = "rewardlix_ownerTheme";

/**
 * Apply a theme by name.
 * Sets data-theme on <html>.
 *
 * @param {string} name
 */
export function setTheme(name) {
  const theme = VALID_THEMES.includes(name) ? name : DEFAULT_THEME;
  document.documentElement.dataset.theme = theme;
}

/**
 * Get current theme string based on HTML attribute (or default).
 */
export function getCurrentTheme() {
  const ds = document.documentElement.dataset.theme;
  return ds && VALID_THEMES.includes(ds) ? ds : DEFAULT_THEME;
}

// ─── Customer theme ───────────────────────────────────────────────────────────

/**
 * Toggle between light and dark for the customer app.
 * Persists the choice in localStorage.
 */
export function toggleCustomerTheme() {
  const current = getCurrentTheme();
  const newTheme = current === "gold-dark" ? "gold-light" : "gold-dark";
  localStorage.setItem(CUSTOMER_PREF_KEY, newTheme);
  setTheme(newTheme);
}

/**
 * Read the stored customer theme preference (defaults to gold-light).
 *
 * @returns {string}
 */
export function getCustomerTheme() {
  const stored = localStorage.getItem(CUSTOMER_PREF_KEY);
  return VALID_THEMES.includes(stored) ? stored : DEFAULT_THEME;
}

/**
 * Apply the stored customer theme preference.
 * Called on SPA boot for customers.
 */
export function resetTheme() {
  setTheme(getCustomerTheme());
}

// ─── Owner theme ──────────────────────────────────────────────────────────────

/**
 * Read the stored owner app theme preference (defaults to gold-light).
 *
 * @returns {string}
 */
export function getOwnerTheme() {
  const stored = localStorage.getItem(OWNER_PREF_KEY);
  return VALID_THEMES.includes(stored) ? stored : DEFAULT_THEME;
}

/**
 * Apply the stored owner app theme preference.
 * Called on SPA boot when the user is an owner.
 */
export function initOwnerTheme() {
  setTheme(getOwnerTheme());
}

/**
 * Toggle between light and dark for the owner app.
 * Persists the choice in localStorage.
 */
export function toggleOwnerTheme() {
  const current = getCurrentTheme();
  const newTheme = current === "gold-dark" ? "gold-light" : "gold-dark";
  localStorage.setItem(OWNER_PREF_KEY, newTheme);
  setTheme(newTheme);
}

// ─── App startup ──────────────────────────────────────────────────────────────

/**
 * Call once at app startup (before auth resolves).
 * Defaults to gold-light; the owner boot block will re-apply the owner pref
 * once the role is known.
 */
export function initTheme() {
  setTheme(DEFAULT_THEME);
}

// ─── Landing page (shop) ──────────────────────────────────────────────────────

/**
 * Map a Firestore `landingTheme` DB value to a valid CSS theme name.
 * Defaults to gold-light (no customer-pref override — that is session-only
 * on the landing page via its own local toggle).
 *
 * @param {string|undefined} dbValue
 * @returns {string} CSS theme name
 */
export function shopThemeFromDb(dbValue) {
  if (VALID_THEMES.includes(dbValue)) return dbValue;
  if (dbValue === "dark") return "gold-dark";
  return DEFAULT_THEME;
}
